import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getBackendUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

// ✅ Use native fetch to avoid rrweb-recorder clone conflicts
const safeFetch = typeof window !== 'undefined' 
  ? (window.__nativeFetch__ || window.fetch.bind(window))
  : fetch;

/**
 * Premium Notification Toast Component
 * Displays in-app notifications with Framer Motion animations
 */
const NotificationToast = () => {
  const [notifications, setNotifications] = useState([]);
  const dismissedIdsRef = useRef(new Set());
  const navigate = useNavigate();

  // ✅ useCallback with stable dependencies
  const fetchUnreadNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // ✅ Silently skip if not authenticated
      if (!token) return;

      const response = await safeFetch(`${BACKEND_URL}/api/notifications/unread`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // ✅ Handle non-2xx silently — 401 (token expiré), 404 (user JWT stale),
      //   5xx (backend down). Tous ces cas sont non-critiques pour la
      //   notification toast (feature secondaire). Le navigateur log l'erreur
      //   réseau automatiquement, on ne peut pas la supprimer côté JS, mais
      //   on évite tout traitement défensif inutile et tout spam applicatif.
      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const newNotifications = (data.notifications || []).filter(
        n => !dismissedIdsRef.current.has(n.id)
      );
      setNotifications(newNotifications);
    } catch (error) {
      // ✅ Silently ignore network errors for notifications (non-critical)
      console.debug('[NotificationToast] Fetch skipped:', error.message);
    }
  }, []);

  useEffect(() => {
    // Initial fetch after mount
    const timeoutId = setTimeout(() => {
      fetchUnreadNotifications();
    }, 100);
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadNotifications, 30000);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, [fetchUnreadNotifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await safeFetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.debug('[NotificationToast] Mark as read skipped:', error.message);
    }
  }, []);

  const handleDismiss = useCallback((notificationId) => {
    dismissedIdsRef.current.add(notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    markAsRead(notificationId);
  }, [markAsRead]);

  const handleViewDashboard = useCallback((notificationId) => {
    handleDismiss(notificationId);
    navigate('/affiliate-dashboard');
  }, [handleDismiss, navigate]);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-md">
      <AnimatePresence>
        {notifications.slice(0, 3).map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="relative"
          >
            {/* Glow background */}
            <motion.div
              animate={{
                boxShadow: [
                  '0 0 20px rgba(168, 85, 247, 0.3)',
                  '0 0 40px rgba(168, 85, 247, 0.5)',
                  '0 0 20px rgba(168, 85, 247, 0.3)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-2xl p-[2px]"
            >
              <div className="bg-white rounded-2xl p-4 relative overflow-hidden">
                {/* Sparkle decoration */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="absolute top-2 right-2 opacity-20"
                >
                  <Sparkles className="text-purple-600" size={16} />
                </motion.div>

                {/* Close button */}
                <button
                  onClick={() => handleDismiss(notification.id)}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={18} className="text-gray-500" />
                </button>

                {/* Content */}
                <div className="pr-8">
                  <h4 className="font-bold text-lg text-gray-800 mb-1">
                    {notification.title}
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {notification.message}
                  </p>

                  {/* Badge data if available */}
                  {notification.data && notification.data.old_rate !== undefined && (
                    <div className="flex items-center gap-2 mb-3 text-xs">
                      <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">
                        {notification.data.old_rate}%
                      </span>
                      <span className="text-purple-600">→</span>
                      <span className="px-2 py-1 bg-purple-100 rounded text-purple-700 font-semibold">
                        {notification.data.new_rate}%
                      </span>
                    </div>
                  )}

                  {/* Action button */}
                  <button
                    onClick={() => handleViewDashboard(notification.id)}
                    className="flex items-center gap-2 text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    <span>Voir mon dashboard</span>
                    <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationToast;
